import type { InviteRepModel } from '../app/components/reps/invite-rep-dialog.tsx';
import type { MeetingMediaModel } from '../app/types/meeting.type.ts';
import type {
  RepresentativeAdminDetailsModel,
  RepresentativeModel,
  RepresentativeSearchModel,
  RepresentativeSearchParams,
} from '../app/types/representative.type.ts';
import type { RepresentativeStatusModel } from '../app/types/representative-status.type.ts';
import baseApi, { CacheTag } from './meeting-md-api-slice';

const representativeApiSlice = baseApi.injectEndpoints({
  endpoints: builder => ({
    putRepresentative: builder.mutation<RepresentativeModel, RepresentativeModel>({
      query: put => ({
        url: `/representative`,
        method: 'PUT',
        body: put,
      }),
      invalidatesTags: [CacheTag.CurrentUser, CacheTag.User, CacheTag.AccountReps],
    }),
    fetchRepresentative: builder.query<RepresentativeModel, string>({
      query: key => ({ url: `/Representative/${key}` }),
      providesTags: [CacheTag.CurrentUser, CacheTag.User, CacheTag.AccountReps],
    }),
    fetchRepresentativeAdminDetails: builder.query<RepresentativeAdminDetailsModel, string>({
      query: key => ({ url: `/Representative/${key}/AdminDetails` }),
    }),
    fetchSearchRepresentatives: builder.query<
      RepresentativeSearchModel[],
      RepresentativeSearchParams | URLSearchParams
    >({
      query: params => ({ url: '/Representative', params }),
      providesTags: [CacheTag.User, CacheTag.AccountReps],
    }),
    fetchRepresentativeMedia: builder.query<MeetingMediaModel[], string>({
      query: key => ({ url: `/Representative/media/${key}` }),
      providesTags: [CacheTag.Events],
    }),
    postInviteRepresentative: builder.mutation<void, InviteRepModel>({
      query: post => ({
        url: `/representative/invite`,
        method: 'POST',
        body: post,
      }),
    }),
    deleteRepresentative: builder.mutation<void, string>({
      query: key => ({
        url: `/representative/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CacheTag.User, CacheTag.AccountReps],
    }),
    fetchRepresentativeStatuses: builder.query<RepresentativeStatusModel[], void>({
      query: () => ({ url: '/representative/statuses' }),
    }),
    fetchRepSubscriptionStatuses: builder.query<string[], void>({
      query: () => ({ url: 'representative/subscriptionstatuses' }),
      providesTags: [CacheTag.User],
    }),
    fetchCalendarSyncToken: builder.query<{ calendarSyncToken: string }, void>({
      query: () => ({ url: '/representative/CalendarSyncToken' }),
    }),
  }),
});

export const {
  usePutRepresentativeMutation,
  useFetchRepresentativeQuery,
  useFetchRepresentativeAdminDetailsQuery,
  useFetchSearchRepresentativesQuery,
  useFetchRepresentativeMediaQuery,
  usePostInviteRepresentativeMutation,
  useDeleteRepresentativeMutation,
  useFetchRepresentativeStatusesQuery,
  useFetchRepSubscriptionStatusesQuery,
  useFetchCalendarSyncTokenQuery,
} = representativeApiSlice;
